import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRight } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { userApi, type OnboardingPayload } from '@/entities/user';
import { ApiError } from '@/shared/api/types';
import { useAuthStore } from '@/shared/stores/authStore';
import { AUTH_FORM_SIZE } from '@/shared/config/auth-form';
import { ROUTES } from '@/shared/config/constants';
import { SignupLayout } from './SignupLayout';
import { signupSteps } from './stepsRegistry';
import { useSignupForm, clearSignupDraft } from './useSignupForm';
import type { SignupFormValues } from './schema';

const toOnboardingPayload = (values: SignupFormValues): OnboardingPayload => ({
  tipoPerfil: values.tipoPerfil,
  cargo: values.cargo,
  empresa: values.empresa,
  segmento: values.segmento,
  estado: values.estado,
  faturamento: values.faturamento,
  tamanhoTime: values.tamanhoTime,
  frequenciaNetworking: values.frequenciaNetworking,
  tagsBuscaIds: values.tagsBuscaIds,
  tagsOfertaIds: values.tagsOfertaIds,
  gargalo: values.gargalo,
  diagnosticoFase: values.diagnosticoFase,
  consentSponsor: values.consentSponsor,
});

const clampStepIndex = (index: number): number => {
  if (Number.isNaN(index) || index < 0) return 0;
  if (index > signupSteps.length - 1) return signupSteps.length - 1;
  return index;
};

export const SignupFlow = () => {
  const navigate = useNavigate();
  const registerUser = useAuthStore((s) => s.register);
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  const { methods, initialStepIndex, initialAccountCreated, persist } = useSignupForm();

  const [currentStepIndex, setCurrentStepIndex] = useState(() => clampStepIndex(initialStepIndex));
  const [accountCreated, setAccountCreated] = useState(initialAccountCreated);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    if (user?.onboardingCompleto) {
      navigate(ROUTES.APP.MATCHES, { replace: true });
    }
  }, [user?.onboardingCompleto, navigate]);

  const totalSteps = signupSteps.length;
  // clampStepIndex já garante 0 <= currentStepIndex <= totalSteps - 1
  const currentStep = signupSteps[currentStepIndex]!;
  const isLast = currentStepIndex === totalSteps - 1;
  const StepComponent = currentStep.Component;

  // Watch dos campos do step atual + erros para habilitar/desabilitar o botao.
  // Boolean(consentSponsor) eh aceito como valido independente de marcado ou nao;
  // os demais campos exigem valor preenchido.
  const watchedValues = useWatch({ control: methods.control, name: currentStep.fields });
  const fieldErrors = currentStep.fields.map((f) => methods.formState.errors[f]);
  const stepIsValid = useMemo(() => {
    const hasError = fieldErrors.some(Boolean);
    if (hasError) return false;
    return currentStep.fields.every((field, idx) => {
      const value = watchedValues[idx];
      if (field === 'consentSponsor') return typeof value === 'boolean';
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'number') return !Number.isNaN(value);
      return typeof value === 'string' ? value.trim().length > 0 : value != null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues, JSON.stringify(fieldErrors), currentStep]);

  const submitMutation = useMutation({
    mutationFn: userApi.submitOnboarding,
    onSuccess: ({ user: nextUser }) => {
      setUser({
        id: nextUser.id,
        email: nextUser.email,
        nome: nextUser.nome,
        fotoUrl: nextUser.fotoUrl,
        onboardingCompleto: nextUser.onboardingCompleto,
        plano: nextUser.plano,
      });
      clearSignupDraft();
      toast.success('Perfil pronto! Bem-vindo ao Match.');
      navigate(ROUTES.APP.MATCHES, { replace: true });
    },
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.message : 'Não foi possível salvar. Tente novamente.';
      toast.error(message);
    },
  });

  const goToStep = useCallback(
    (nextIndex: number) => {
      const clamped = clampStepIndex(nextIndex);
      setCurrentStepIndex(clamped);
      persist(clamped, accountCreated);
    },
    [persist, accountCreated],
  );

  const handlePrev = useCallback(() => {
    if (currentStepIndex === 0) return;
    goToStep(currentStepIndex - 1);
  }, [currentStepIndex, goToStep]);

  const handleNext = useCallback(async () => {
    if (advancing) return;
    if (!stepIsValid) return;

    const fields = currentStep.fields;
    const valid = await methods.trigger(fields, { shouldFocus: true });
    if (!valid) return;

    setAdvancing(true);
    try {
      if (currentStep.id === 'senha' && !accountCreated) {
        const { nome, email, senha } = methods.getValues();
        try {
          await registerUser({ nome, email, senha });
          setAccountCreated(true);
          persist(currentStepIndex + 1, true);
        } catch (err) {
          const message =
            err instanceof ApiError ? err.message : 'Falha ao criar conta. Tente novamente.';
          toast.error(message);
          return;
        }
      }

      if (isLast) {
        const values = methods.getValues();
        await submitMutation.mutateAsync(toOnboardingPayload(values));
        return;
      }

      goToStep(currentStepIndex + 1);
    } finally {
      setAdvancing(false);
    }
  }, [
    accountCreated,
    advancing,
    currentStep,
    currentStepIndex,
    goToStep,
    isLast,
    methods,
    persist,
    registerUser,
    stepIsValid,
    submitMutation,
  ]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      const target = event.target as HTMLElement | null;
      if (target?.tagName === 'TEXTAREA' && !(event.metaKey || event.ctrlKey)) return;
      if (target?.tagName === 'BUTTON') return;
      event.preventDefault();
      void handleNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleNext]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleNext();
        }}
        noValidate
      >
        <SignupLayout
          currentStepIndex={currentStepIndex}
          totalSteps={totalSteps}
          title={currentStep.title}
          subtitle={currentStep.subtitle}
          canGoPrev={currentStepIndex > 0}
          onPrev={handlePrev}
          footer={
            <div className="flex flex-col items-stretch gap-3">
              <Button
                type="submit"
                size={AUTH_FORM_SIZE}
                color="primary"
                isLoading={advancing || submitMutation.isPending}
                isDisabled={!stepIsValid}
                iconTrailing={isLast ? undefined : ArrowRight}
                className="w-full bg-black ring-black hover:bg-gray-900 data-loading:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLast ? 'Criar minha conta' : 'Continuar'}
              </Button>
              <p className="hidden text-center text-xs text-gray-500 sm:block">
                Pressione{' '}
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-700">
                  Enter ↵
                </kbd>{' '}
                para continuar
              </p>
            </div>
          }
        >
          <div key={currentStep.id}>
            <StepComponent />
          </div>
        </SignupLayout>
      </form>
    </FormProvider>
  );
};
